import { createStructuredSelector } from 'reselect';
import { ReactiveInjectable, reacts, injectable, inject } from 'src/features/ioc';
import type { WidgetDataModel } from 'src/models/WidgetDataModel';
import { container } from 'src/StaticContainer';
import type { RootState } from 'src/store';
import { onTabChannelChange, resetChannelData } from 'src/store/actions/widgetAndChannel';
import type { ReactBindings } from 'src/types/bindings';

const makeSelectWidgetData = (tabId: string) =>
    createStructuredSelector({
        tab: (state: RootState) => state.tab.byId[tabId],
    });

type WidgetData = ReturnType<ReturnType<typeof makeSelectWidgetData>>;

const log = container.get('Logger').getSubLogger({ name: 'WidgetDataModel' });

@injectable()
export class WidgetDataModelImpl extends ReactiveInjectable implements WidgetDataModel {
    #widgetData!: WidgetData;
    @inject('Store') protected readonly store!: ReactBindings['Store'];

    changeChannel(channelId: string | null) {
        this.store.dispatch(onTabChannelChange(this.#widgetData.tab.id, channelId));
    }

    resetChannelData() {
        this.store.dispatch(resetChannelData(this.#widgetData.tab.id));
    }

    get channelId() {
        return this.#widgetData.tab.channelId;
    }

    get displayName() {
        return this.#widgetData.tab.name || '';
    }

    init(tabId: string) {
        const selectWidgetData = makeSelectWidgetData(tabId);
        this.#widgetData = selectWidgetData(this.store.getState());

        const unsubscribe = this.store
            .subscribeSelector((state) => {
                // Check if the tab still exists before trying to select it
                const tabExists = state.tab.byId[tabId] !== undefined;
                if (!tabExists) {
                    // Tab has been removed, return null to indicate no update needed
                    return null;
                }
                return selectWidgetData(state);
            })
            .handle((widgetData) => {
                // Only update if widgetData is not null (tab still exists)
                if (widgetData !== null) {
                    this.widgetDataReactor = widgetData;
                }
            });

        this.disposableStack.use({
            [Symbol.dispose]: () => {
                if (TRACE) {
                    log.trace({ message: 'disposing widget data store subscription' });
                }
                unsubscribe();
            },
        });
    }

    get tabId() {
        return this.#widgetData.tab.id;
    }

    get containerId() {
        return this.#widgetData.tab.containerId;
    }

    get tab() {
        return this.#widgetData.tab;
    }

    get widget() {
        return this.#widgetData.tab.widget;
    }

    @reacts set widgetDataReactor(widgetData: WidgetData) {
        this.#widgetData = widgetData;
    }
}
